// ToastCard: how a toast is drawn. A result landing over the page: the overlay step with the one
// drop shadow, the tone as a tint plus an icon whose shape says the same, and the message in
// plain words. It springs in from its edge and leaves faster than it came. A success draws its
// check and lets one pass of light cross it: the toast's moment of charm, on the result that
// earned it. The toast system (components/Toast) and the update prompt both draw with this.
// docs/operations/design-standard.md, "Feedback".

import React, { useEffect, useState } from 'react';
import { IconButton } from './Button';
import Tooltip from './Tooltip';
import { IconCheck, IconClose } from './icons';
import { TONE, type Tone } from './Alert';

export interface ToastCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'role'> {
  tone: Tone;
  children: React.ReactNode;
  /** Drawn arrived. False draws it not yet arrived, or leaving, and lets the pointer through. */
  open: boolean;
  /** The edge it arrives from and leaves toward. */
  from?: 'top' | 'bottom';
  /** Replaces the tone's icon. */
  icon?: React.ReactNode;
  /** A key that acts on the result (Undo, Update), a kit Button. */
  action?: React.ReactNode;
  /** Draws the close key. Leave it out for a notice that must not be put away. */
  onDismiss?: () => void;
  /** Each toast is its own alert; a card inside a live region of its own takes none. */
  role?: 'alert' | 'status';
}

export const ToastCard: React.FC<ToastCardProps> = ({
  tone, children, open, from = 'top', icon, action, onDismiss, role, className = '', ...rest
}) => {
  const t = TONE[tone];
  const [sweep, setSweep] = useState(false);

  useEffect(() => {
    if (!open || tone !== 'success') return undefined;
    setSweep(true);
    // Timed rather than on animationend, which never arrives when the animation does not run.
    const timer = window.setTimeout(() => setSweep(false), 800);
    return () => window.clearTimeout(timer);
  }, [open, tone]);

  const away = from === 'top' ? '-translate-y-2' : 'translate-y-2';
  const mark = icon || (tone === 'success' ? <IconCheck draw className="h-5 w-5" /> : <t.Icon className="h-5 w-5" />);

  return (
    <div
      role={role}
      data-tone={tone}
      className={`relative isolate flex items-start gap-3 overflow-hidden rounded-overlay bg-surface-container-high px-4 py-3 text-on-surface shadow-overlay transition-[opacity,transform] motion-reduce:translate-y-0 motion-reduce:scale-100 motion-reduce:transition-[opacity] max-sm:px-5 max-sm:py-4 ${
        open ? 'pointer-events-auto translate-y-0 scale-100 opacity-100 duration-spring ease-spring' : `${away} pointer-events-none scale-95 opacity-0 duration-exit ease-exit`
      } ${className}`}
      {...rest}
    >
      <span aria-hidden="true" className={`pointer-events-none absolute inset-0 -z-10 ${t.wash}`} />
      {sweep && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.08),transparent)] motion-safe:animate-sheen motion-reduce:animate-flare"
        />
      )}
      <span className={`flex flex-none max-sm:mt-0.5 ${t.ink}`}>{mark}</span>
      <p className="m-0 min-w-0 flex-1 break-words text-sm font-medium leading-5 max-sm:text-base max-sm:leading-6">{children}</p>
      {action && <span className="-my-1 flex flex-none items-center gap-2 max-sm:-my-0.5">{action}</span>}
      {onDismiss && (
        <Tooltip label="Dismiss notification">
          <IconButton label="Dismiss notification" size="sm" onClick={onDismiss} className="-my-1 -mr-2 max-sm:-my-0.5">
            <IconClose />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
};

export default ToastCard;
